import { SaveArtefact } from '@modules/common/components/SaveArtefact';
import { SaveArtefactConfirmDialog } from '@modules/common/components/SaveArtefact/SaveArtefactConfirmDialog';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import { ArtefactType } from '@modules/common/types';
import { useSaveSearchDashboard } from '@modules/feed/hooks';
import { useUpdateSearchDashboardAction } from '@modules/feed/hooks/useUpdateSearchDashboardAction';
import { useSearchStore } from '@modules/feed/store/searchStore';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { shallow } from 'zustand/shallow';

const trackSaveAsNewCTAEvents = createAnalyticsTracker('saveAsNewCTA');

export const UpdateSearchAction = () => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const [popoverAnchorEl, setPopoverAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [isSaveArtefactFormOpen, setIsSaveArtefactFormOpen] = useState(false);
  const [artefactRoute, setSaveArtefactRoute] = useState<string | null>(null);
  const hasChangesToBeSaved = useSearchStore(
    ({ visual: { hasChangesToBeSaved } = {} }) => hasChangesToBeSaved,
  );
  const { title, viewToken } = useSearchStore(
    ({ data: { title, viewToken } = {} }) => {
      return {
        title,
        viewToken,
      };
    },
    shallow,
  );

  const isPopoverOpen = Boolean(popoverAnchorEl);

  const { onUpdateSearchDashboard, isLoading } =
    useUpdateSearchDashboardAction();
  const { onSaveSearchDashboard } = useSaveSearchDashboard();

  const onSaveSearchArtefact = async (name: string, scope: Scope) => {
    const artefactDetails = await onSaveSearchDashboard(name, scope, false);
    setSaveArtefactRoute(artefactDetails.artefactRoute);

    return artefactDetails;
  };

  const closeForm = () => setIsSaveArtefactFormOpen(false);

  const openForm = () => {
    closePopover();
    setIsSaveArtefactFormOpen(true);
    trackSaveAsNewCTAEvents({
      eventName: 'Save As New CTA Clicked - Search page',
      artefactName: title,
      artefactViewToken: viewToken,
      isUserArtefactOwner: true,
    });
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const closePopover = () => setPopoverAnchorEl(null);

  const onUpdateAnalysis = () => {
    closePopover();
    onUpdateSearchDashboard();
  };

  const popoverID = isPopoverOpen ? 'simple-popover' : undefined;

  const renderSaveActionButton = () => {
    return (
      <LoadingButton
        variant="outlined"
        onClick={handlePopoverOpen}
        loading={isLoading}
        loadingPosition="start"
        disabled={!hasChangesToBeSaved}
        startIcon={<BookmarkRoundedIcon />}
        endIcon={<ExpandMoreIcon />}
      >
        Save
      </LoadingButton>
    );
  };

  return (
    <>
      {!hasChangesToBeSaved ? (
        <Tooltip title="There are no changes to save">
          <Stack>{renderSaveActionButton()}</Stack>
        </Tooltip>
      ) : (
        renderSaveActionButton()
      )}
      <Popover
        id={popoverID}
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton sx={{ py: 0.5 }} onClick={onUpdateAnalysis}>
              <ListItemText primary="Update" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton sx={{ py: 0.5 }} onClick={openForm}>
              <ListItemText primary="Save As New" />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
      <SaveArtefactConfirmDialog
        artefactRoute={artefactRoute}
        fullWidth
        maxWidth="sm"
        PaperProps={{ elevation: 3 }}
        open={isSaveArtefactFormOpen}
        onClose={closeForm}
      >
        <SaveArtefact
          headerText={`Save New ${isSearchToFeedEnabled ? 'Feed' : 'Search'}`}
          buttonText="Save as new"
          onSubmit={onSaveSearchArtefact}
          onClose={closeForm}
          artefactType={ArtefactType.Search}
        />
      </SaveArtefactConfirmDialog>
    </>
  );
};
